"use client"

import React, { useState } from "react"

const Table = ({ columns, data, pagination = true, itemsPerPage = 5 }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })

  // Sorting logic
  const sortedData = React.useMemo(() => {
    const sortableData = [...data]
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1
        }
        return 0
      })
    }
    return sortableData
  }, [data, sortConfig])

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem)

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr style={{ background: "#f9f9f9" }}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && requestSort(column.key)}
                  style={{
                    padding: "12px 15px",
                    textAlign: "left",
                    borderBottom: "1px solid #eee",
                    cursor: column.sortable ? "pointer" : "default",
                    position: "relative",
                  }}
                >
                  {column.label}
                  {sortConfig.key === column.key && (
                    <span style={{ marginLeft: "5px" }}>{sortConfig.direction === "ascending" ? "↑" : "↓"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentItems.map((row, index) => (
              <tr
                key={index}
                style={{
                  borderBottom: "1px solid #eee",
                  background: index % 2 === 0 ? "white" : "#fafafa",
                  transition: "background 0.2s",
                  ":hover": { background: "#f5f5f5" },
                }}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      padding: "12px 15px",
                      textAlign: column.align || "left",
                    }}
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
          }}
        >
          <div>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, data.length)} of {data.length} entries
          </div>
          <div style={{ display: "flex" }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: "5px 10px",
                margin: "0 5px",
                background: currentPage === 1 ? "#f5f5f5" : "white",
                border: "1px solid #ddd",
                borderRadius: "3px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                style={{
                  padding: "5px 10px",
                  margin: "0 5px",
                  background: currentPage === index + 1 ? "linear-gradient(45deg, #FF6600,#B8529E)" : "white",
                  color: currentPage === index + 1 ? "white" : "#333",
                  border: "1px solid #ddd",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: "5px 10px",
                margin: "0 5px",
                background: currentPage === totalPages ? "#f5f5f5" : "white",
                border: "1px solid #ddd",
                borderRadius: "3px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Table
